import { useTranslation } from 'react-i18next'

import type { LearnArticleBodyProps } from '@/features/aprender/articles'
import { COSTE_POR_PLATO_IMG } from '@/features/aprender/learnArticleConstants'
import { DashList, Formula, LearnArticleLink, LearnSeriesNav, TipiLink } from '@/features/aprender/learnArticleShared'
import { LandingDemoPlayer } from '@/features/demos/LandingDemoPlayer'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article as articleUi } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const k = (f: string) => `learn.articles.como-hacer-un-escandallo-paso-a-paso.${f}`

const IMG = COSTE_POR_PLATO_IMG

const SOURCES: { labelKey: string; fallback: string }[] = [
    {
        labelKey: 'sources.1',
        fallback:
            'Tabla de hamburguesa (3,95 € por ración) y ejemplos de huevos, aceite y merma de pescado: elaboración propia de Tipi en la serie de coste por plato. No son datos de un local concreto.',
    },
    {
        labelKey: 'sources.2',
        fallback:
            'Uso del precio sin IVA para margen y food cost: coherente con el tratamiento del IVA repercutido como tránsito, no como ingreso del local (ver Agencia Tributaria, tipos de IVA en hostelería).',
    },
]

export function EscandalloPasoAPasoArticle({ article }: LearnArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'ingredientes', label: t(k('toc.1'), '01. Lista completa de ingredientes') },
        { id: 'cantidades', label: t(k('toc.2'), '02. Gramajes que cocina puede repetir') },
        { id: 'precio-unitario', label: t(k('toc.3'), '03. Precio unitario al día') },
        { id: 'sin-iva', label: t(k('toc.4'), '04. Costes y venta sin IVA') },
        { id: 'merma', label: t(k('toc.5'), '05. Merma y rendimiento real') },
        { id: 'suma', label: t(k('toc.6'), '06. Coste por ración (el que sale de verdad)') },
        { id: 'rompe', label: t(k('toc.7'), '07. Dónde se rompe el escandallo') },
    ]

    return (
        <div className={articleUi.page}>
            <article className={articleUi.shell}>
                <ArticleCoverHero
                    category={article.category}
                    title={article.title}
                    dek={
                        <>
                            {t(
                                k('dek'),
                                'No necesitas ser financiero ni enamorar a las hojas de cálculo. Necesitas tres cosas: pesar, apuntar y trabajar con precios actualizados.',
                            )}
                        </>
                    }
                    imageSrc={`${IMG}/bascula.webp`}
                    imageAlt={t(k('heroAlt'), 'Profesional de cocina pesando ingredientes en una báscula')}
                    byline={
                        <ArticleByline
                            authorName={article.authorName}
                            readingMinutes={article.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />
                <ArticleReadingLayout
                    intro={
                        <div className={articleUi.proseIntro}>
                            <p className={cn(articleUi.p, articleUi.pGapMd, 'text-article-muted-soft')}>
                                {t(k('updated'), 'Actualizado el 13 de septiembre de 2026.')}
                            </p>
                            <p className={articleUi.p}>
                                {t(k('intro1Before'), 'Si llegas desde ')}
                                <LearnArticleLink slug='como-calcular-coste-de-un-plato'>
                                    {t(k('intro1Link'), 'la guía sobre el coste de un plato')}
                                </LearnArticleLink>
                                {t(
                                    k('intro1After'),
                                    ', ya sabes por qué el cálculo a ojo puede costarte miles de euros al año. Aquí vamos a lo concreto: cómo construir el escandallo de una receta, paso a paso, con el ejemplo de la hamburguesa de Carlos.',
                                )}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                                {t(
                                    k('intro2Before'),
                                    'Cuando termines, tendrás el coste por ración. Después podrás pasar a ',
                                )}
                                <LearnArticleLink slug='food-cost-y-margen-bruto-restaurante'>
                                    {t(k('intro2Link'), 'food cost y margen bruto')}
                                </LearnArticleLink>
                                {t(k('intro2After'), ' para saber cuánto ganas realmente con cada venta.')}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                                {t(
                                    k('intro3Before'),
                                    'Si lo que buscas es hacerlo por voz desde la cocina, sin libreta, ve a ',
                                )}
                                <LocaleLink to='/como-hacer-un-escandallo-sin-ordenador' className={articleUi.link}>
                                    {t(k('intro3Link'), 'escandallo por voz sin ordenador')}
                                </LocaleLink>
                                {t(k('intro3After'), '.')}
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <h2 id='ingredientes' className={articleUi.h2}>
                        <span className={articleUi.stepNum}>01</span>{' '}
                        {t(k('h2.ingredientes'), 'Lista completa: también lo que «cuesta poco»')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.ingredientes.1'),
                                'Y cuando digo todos, significa todos. No solo la carne, el pescado o el ingrediente principal. También debes incluir:',
                            )}
                        </p>
                        <DashList
                            variant='tight'
                            items={[
                                t(k('list.ingredientes.1'), 'Aceite, sal y especias.'),
                                t(k('list.ingredientes.2'), 'Salsas, pan y guarniciones.'),
                                t(k('list.ingredientes.3'), 'Decoraciones y consumibles directamente asociados.'),
                                t(k('list.ingredientes.4'), 'Envases y bolsas en los pedidos de delivery o take away.'),
                            ]}
                        />
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.ingredientes.2'),
                                'Uno de los errores más habituales es ignorar los ingredientes pequeños porque «eso cuesta muy poco». Y es verdad. Dos gramos de sal cuestan muy poco. El problema es que la sal no suele ser el único elemento que se olvida. También se olvida parte del aceite. La salsa. El pan. La guarnición. El pequeño envase. El sobre de ketchup. La servilleta especial. La decoración.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.ingredientes.3'),
                                'Y cuando sumas todos esos pequeños costes y los multiplicas por miles de servicios, dejan de ser tan pequeños. No hace falta contar cada grano de pimienta. Sí hace falta una estimación razonable de todo lo que sale en la elaboración.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.ingredientes.trap'),
                                'Trampa: dejar fuera el aceite de freír o el pan «porque ya está en otra partida». Acción: cierra la lista con el plato delante, no desde memoria. Lo que no está en el escandallo no desaparece: lo sigues pagando sin verlo.',
                            )}
                        </p>
                    </div>

                    <h2 id='cantidades' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        <span className={articleUi.stepNum}>02</span>{' '}
                        {t(k('h2.cantidades'), 'Gramajes que cocina puede repetir')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.cantidades.1'),
                                'Aquí se termina el «un poco», «un puñado», «más o menos», «lo que admita», «hasta que parezca suficiente». Necesitas cantidades concretas: 180 gramos de carne, 250 gramos de patata, 30 gramos de salsa, 10 mililitros de aceite, dos lonchas de queso de 20 gramos cada una, una unidad de pan.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.cantidades.2'),
                                'Cuando cada cocinero utiliza una cantidad diferente, no solo cambia la presentación del plato. También cambia el coste. Supongamos que la ficha técnica indica que una hamburguesa debe llevar 150 gramos de carne, pero en cocina se están utilizando 180 gramos. Estás sirviendo 30 gramos adicionales en cada plato.',
                            )}
                        </p>
                        <Formula>
                            0,30 € × 1.000 hamburguesas ={' '}
                            <strong>{t(k('formula.cantidades.overspend'), '300 € de sobrecoste')}</strong>
                        </Formula>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.cantidades.3'),
                                'Una ficha no está para decorar una carpeta. Está para que el plato lleve la misma cantidad contigo, con el jefe de cocina y con quien entre nuevo. Protege el coste y la experiencia del cliente.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.cantidades.trap'),
                                'Trampa: apuntar 150 g y servir 180 g «porque queda mejor». Acción: pesa tres pases reales esta semana y corrige la ficha o el hábito. El Excel no cocina.',
                            )}
                        </p>
                    </div>

                    <h2 id='precio-unitario' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        <span className={articleUi.stepNum}>03</span>{' '}
                        {t(k('h2.precio-unitario'), 'Precio por kilo, litro o unidad (al día)')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.precio.1'),
                                'Para calcular cuánto cuesta la cantidad utilizada en una receta, necesitas conocer primero el precio del ingrediente en una unidad manejable: euros por kilogramo, euros por litro, euros por unidad.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapSm)}>
                            {t(k('p.precio.2'), 'Compras una caja de 24 huevos por 5,28 €:')}
                        </p>
                        <Formula variant='tight'>5,28 € ÷ 24 = 0,22 € {t(k('formula.perEgg'), 'por huevo')}</Formula>
                        <Formula>2 × 0,22 € = 0,44 € {t(k('formula.twoEggs'), '(dos huevos en la receta)')}</Formula>
                        <p className={cn(articleUi.p, articleUi.pGapSm)}>
                            {t(k('p.precio.3'), 'Compras una garrafa de cinco litros de aceite por 27,50 €:')}
                        </p>
                        <Formula variant='tight'>27,50 € ÷ 5 = 5,50 €/litre</Formula>
                        <Formula variant='tight'>5,50 € ÷ 1.000 = 0,0055 €/ml</Formula>
                        <Formula>
                            30 × 0,0055 € = 0,165 € {t(k('formula.oilPerPortion'), '(≈0,17 € de aceite por ración)')}
                        </Formula>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.precio.4'),
                                'Detalle crítico: precios de compra actualizados. Un escandallo con precios del año pasado te da rentabilidad falsa. El plato no sabe que no actualizaste el Excel.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.precio.trap'),
                                'Trampa: copiar el precio de la web del proveedor sin mirar el último albarán. Acción: toma el precio del albarán más reciente de ese producto antes de cerrar la ficha.',
                            )}
                        </p>
                    </div>

                    <h2 id='sin-iva' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        <span className={articleUi.stepNum}>04</span>{' '}
                        {t(k('h2.sin-iva'), 'Costes y venta sin IVA (si no, el margen miente)')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.sin-iva.1'),
                                'Cuando analizas ventas, costes y márgenes debes utilizar los importes sin IVA. ¿Por qué? Porque el IVA que cobras al cliente no es un ingreso real de tu restaurante. Lo recaudas para después liquidarlo con Hacienda.',
                            )}
                        </p>
                        <Formula>
                            14,50 € ÷ 1,10 ={' '}
                            <span className={articleUi.em}>13,18 € {t(k('formula.exVat'), 'sin IVA')}</span>
                        </Formula>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.sin-iva.2Before'),
                                'Aunque el cliente haya pagado 14,50 €, para food cost y margen usas los 13,18 € netos. Mezclar IVA es un error habitual. Verás el cálculo completo en ',
                            )}
                            <LearnArticleLink slug='food-cost-y-margen-bruto-restaurante'>
                                {t(k('p.sin-iva.2Link'), 'food cost y margen bruto')}
                            </LearnArticleLink>
                            {t(k('p.sin-iva.2After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.sin-iva.trap'),
                                'Trampa: restar IVA solo a la venta y dejar el coste con IVA «porque viene así en la factura». Acción: trabaja ambos lados sin IVA. Si tu proveedor desglosa, usa la base.',
                            )}
                        </p>
                    </div>

                    <h2 id='merma' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        <span className={articleUi.stepNum}>05</span>{' '}
                        {t(k('h2.merma'), 'Merma y rendimiento: el kilo útil, no el comprado')}
                    </h2>
                    <div className={articleUi.figureSection}>
                        <div className={articleUi.figureFrame3x2}>
                            <img src={`${IMG}/merma.webp`} alt={t(k('mermaAlt'), 'Limpieza de pescado en la cocina')} />
                        </div>
                    </div>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.merma.1'),
                                'Este es uno de los lugares donde más dinero puede esconderse. No todo lo que compras termina en el plato. Un pescado tiene cabeza, espinas y vísceras. Una carne puede perder grasa, hueso o agua. Las verduras se pelan. Algunos alimentos pierden peso durante la cocción.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.merma.2'),
                                'Supongamos que compras pescado a 12 €/kg. Después de limpiarlo, solamente aprovechas el 80 %:',
                            )}
                        </p>
                        <Formula variant='tight'>
                            12 € ÷ 0,80 = <span className={articleUi.em}>15 €/kg {t(k('formula.usable'), 'útil')}</span>
                        </Formula>
                        <Formula variant='tight'>
                            0,180 kg × 15 €/kg = 2,70 € {t(k('formula.withWaste'), '(con merma)')}
                        </Formula>
                        <Formula variant='tight'>
                            0,180 kg × 12 €/kg = 2,16 € {t(k('formula.withoutWaste'), '(sin calcular la merma)')}
                        </Formula>
                        <Formula variant='alert'>
                            2,70 € − 2,16 € = 0,54 € {t(k('formula.undervaluedPerDish'), 'infravalorados por plato')} →
                            0,54 € × 600 {t(k('formula.portions'), 'raciones')} ={' '}
                            <strong>{t(k('formula.alertDiff'), '324 € de diferencia')}</strong>
                        </Formula>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.merma.3'),
                                'El proveedor no te ha engañado. La merma tampoco. El problema es calcular como si pudieras servir las espinas, la piel y la cabeza.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.merma.trap'),
                                'Trampa: usar un % de merma «de manual» sin pesar tu propio rendimiento. Acción: limpia o cocina un lote, pesa útil vs comprado, y fija tu %.',
                            )}
                        </p>
                    </div>

                    <h2 id='suma' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        <span className={articleUi.stepNum}>06</span>{' '}
                        {t(k('h2.suma'), 'Suma y divide entre las raciones que salen de verdad')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapLg)}>
                            {t(
                                k('p.suma.1'),
                                'Una vez que conoces el coste de cada ingrediente, solo tienes que sumarlos. Veamos el ejemplo de la hamburguesa de Carlos:',
                            )}
                        </p>
                    </div>

                    <div className={articleUi.tableWrap}>
                        <table className={articleUi.table}>
                            <thead>
                                <tr>
                                    <th scope='col'>{t(k('table.col.ingredient'), 'Ingrediente')}</th>
                                    <th scope='col'>{t(k('table.col.qty'), 'Cantidad')}</th>
                                    <th scope='col'>{t(k('table.col.cost'), 'Coste')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr key={1}>
                                    <td>{t(k('table.row.1.ingredient'), 'Pan brioche')}</td>
                                    <td>{t(k('table.row.1.qty'), '1 unidad')}</td>
                                    <td>{t(k('table.row.1.cost'), '0,42 €')}</td>
                                </tr>
                                <tr key={2}>
                                    <td>{t(k('table.row.2.ingredient'), 'Carne de vacuno')}</td>
                                    <td>{t(k('table.row.2.qty'), '180 g')}</td>
                                    <td>{t(k('table.row.2.cost'), '1,98 €')}</td>
                                </tr>
                                <tr key={3}>
                                    <td>{t(k('table.row.3.ingredient'), 'Queso')}</td>
                                    <td>{t(k('table.row.3.qty'), '2 lonchas')}</td>
                                    <td>{t(k('table.row.3.cost'), '0,28 €')}</td>
                                </tr>
                                <tr key={4}>
                                    <td>{t(k('table.row.4.ingredient'), 'Bacon')}</td>
                                    <td>{t(k('table.row.4.qty'), '40 g')}</td>
                                    <td>{t(k('table.row.4.cost'), '0,34 €')}</td>
                                </tr>
                                <tr key={5}>
                                    <td>{t(k('table.row.5.ingredient'), 'Lechuga')}</td>
                                    <td>{t(k('table.row.5.qty'), '20 g')}</td>
                                    <td>{t(k('table.row.5.cost'), '0,05 €')}</td>
                                </tr>
                                <tr key={6}>
                                    <td>{t(k('table.row.6.ingredient'), 'Tomate')}</td>
                                    <td>{t(k('table.row.6.qty'), '40 g')}</td>
                                    <td>{t(k('table.row.6.cost'), '0,09 €')}</td>
                                </tr>
                                <tr key={7}>
                                    <td>{t(k('table.row.7.ingredient'), 'Cebolla')}</td>
                                    <td>{t(k('table.row.7.qty'), '20 g')}</td>
                                    <td>{t(k('table.row.7.cost'), '0,04 €')}</td>
                                </tr>
                                <tr key={8}>
                                    <td>{t(k('table.row.8.ingredient'), 'Salsa casera')}</td>
                                    <td>{t(k('table.row.8.qty'), '30 g')}</td>
                                    <td>{t(k('table.row.8.cost'), '0,16 €')}</td>
                                </tr>
                                <tr key={9}>
                                    <td>{t(k('table.row.9.ingredient'), 'Patatas')}</td>
                                    <td>{t(k('table.row.9.qty'), '250 g')}</td>
                                    <td>{t(k('table.row.9.cost'), '0,40 €')}</td>
                                </tr>
                                <tr key={10}>
                                    <td>{t(k('table.row.10.ingredient'), 'Aceite para freír')}</td>
                                    <td>{t(k('table.row.10.qty'), '60 ml')}</td>
                                    <td>{t(k('table.row.10.cost'), '0,16 €')}</td>
                                </tr>
                                <tr key={11}>
                                    <td>{t(k('table.row.11.ingredient'), 'Sal')}</td>
                                    <td>{t(k('table.row.11.qty'), '2 g')}</td>
                                    <td>{t(k('table.row.11.cost'), '0,01 €')}</td>
                                </tr>
                                <tr key={12}>
                                    <td>{t(k('table.row.12.ingredient'), 'Pimienta y especias')}</td>
                                    <td>{t(k('table.row.12.qty'), '1 g')}</td>
                                    <td>{t(k('table.row.12.cost'), '0,02 €')}</td>
                                </tr>
                                <tr className={articleUi.tableTotal}>
                                    <td>{t(k('table.totalLabel'), 'Coste total por ración')}</td>
                                    <td />
                                    <td>3,95 €</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapSm)}>
                            {t(k('p.suma.2'), 'La fórmula general es:')}
                        </p>
                        <Formula>
                            {t(
                                k('formula.suma.general'),
                                'Coste por ración = Coste total de la receta ÷ Número de raciones reales',
                            )}
                        </Formula>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.suma.3'),
                                'Esto es especialmente importante en elaboraciones grandes como salsas, guisos, tartas, croquetas, caldos, postres y preparaciones previas. Por ejemplo: coste total de una receta de croquetas: 24 €. Rendimiento real: 80 croquetas.',
                            )}
                        </p>
                        <Formula>
                            24 € ÷ 80 = 0,30 € {t(k('formula.perCroqueta'), 'por croqueta')} → 6 × 0,30 € = 1,80 €{' '}
                            {t(k('formula.perPortion'), 'por ración')}
                        </Formula>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.suma.4'),
                                'Regla: no dividas entre las raciones que «deberían» salir. Divide entre las que salen. Sobre el papel siempre salen más croquetas. Luego alguien las hace más grandes y faltan diez.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.suma.trap'),
                                'Trampa: redondear a la baja «para que el food cost quede bonito». Acción: deja el coste feo y verdadero. El arreglo viene después, en precio o en receta.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(k('p.suma.5Before'), 'Con el coste por ración en la mano, el siguiente paso es saber ')}
                            <LearnArticleLink slug='food-cost-y-margen-bruto-restaurante'>
                                {t(k('p.suma.5Link1'), 'cuánto ganas realmente con el plato')}
                            </LearnArticleLink>
                            {t(k('p.suma.5Mid'), '. Y si quieres comprobar que el papel se cumple en cocina, lee ')}
                            <LearnArticleLink slug='coste-teorico-vs-coste-real-cocina'>
                                {t(k('p.suma.5Link2'), 'coste teórico vs coste real')}
                            </LearnArticleLink>
                            {t(k('p.suma.5After'), '.')}
                        </p>
                    </div>

                    <h2 id='rompe' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.rompe'), 'Dónde se rompe el escandallo (aunque esté bien hecho)')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.rompe.1'),
                                'Se rompe cuando nadie lo usa en el pase. Cuando el precio cambió hace tres albaranes. Cuando la merma real no es la del papel. Cuando el delivery lleva envase que no está en la ficha.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.rompe.2'),
                                'También se rompe si escandallas un plato y asumes que el mes entero cuadra: sin inventario, el teórico es intención. El real es lo que consumiste.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.rompe.3Before'), 'Desde ')}
                            <TipiLink />
                            {t(
                                k('p.rompe.3After'),
                                ' puedes dictar la receta y mantener precios al día. No sustituye pesar en cocina: acorta el tiempo entre el dato y la decisión.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.rompe.4'),
                                'Si quieres construir el escandallo sin pelearte con la libreta, cuéntanoslo y lo miramos.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('closing.signoff'),
                                'Escandalla un plato estrella esta semana. El resto puede esperar. Ese número, no.',
                            )}
                        </p>
                    </div>

                    <section id='escandallo-por-voz'>
                        <h2 className={cn(articleUi.h2, articleUi.h2Spaced)}>
                            {t(k('video.title'), 'Misma hamburguesa, dictada: Tipi calcula el coste')}
                        </h2>
                        <p className={articleUi.p}>
                            {t(
                                k('video.intro'),
                                'Los seis pasos de arriba son el método. Si quieres saltarte la libreta, este es el mismo escandallo dictado en la app.',
                            )}
                        </p>
                        <LandingDemoPlayer demoId='escandallo-voz' variant='article' />
                    </section>
                </ArticleReadingLayout>

                <section className={articleUi.cta}>
                    <h2 className={cn(articleUi.h2, articleUi.h2Cta)}>
                        {t(k('h2.cta'), '¿Quieres el escandallo sin pelearte con la libreta?')}
                    </h2>
                    <p className={articleUi.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Con Tipi dictas la receta, actualizas precios y ves el coste por ración. Cuéntanoslo y lo miramos.',
                        )}
                    </p>
                    <div className={articleUi.ctaActionsCenter}>
                        <LocaleLink to='/#form' className={cn(articleUi.btn, articleUi.btnSolid)}>
                            {t(k('cta.button'), 'Empezar con Tipi')}
                        </LocaleLink>
                    </div>
                </section>

                <LearnSeriesNav currentId={article.id} />

                <section className={articleUi.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={articleUi.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={articleUi.sourcesList}>
                        {SOURCES.map(source => (
                            <li key={source.labelKey}>{t(k(source.labelKey), source.fallback)}</li>
                        ))}
                    </ul>
                </section>
            </article>
        </div>
    )
}
